import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchWatches, fetchValidation, deleteWatch, archiveWatch, unarchiveWatch } from "@/lib/api";
import WatchFormSheet from "@/components/WatchFormSheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { InventoryHeader, InventoryFilterBar } from "@/components/inventory/InventoryToolbar";
import InventoryTable from "@/components/inventory/InventoryTable";
import useDensity from "@/lib/useDensity";
import usePageSize from "@/lib/usePageSize";

// filters.status and filters.caliber are arrays (multi-select). Others stay single-valued.
const EMPTY_FILTERS = { brand: "", status: [], caliber: [], movement_type: "", bin_location: "" };

export default function Inventory() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [density, setDensity] = useDensity();
  const [pageSize, setPageSize] = usePageSize();
  // Hydrate state from URL on first render so a back-button from the
  // watch detail page lands you right where you left off — same filters,
  // same page, same sort. Prev/Next on the detail page uses the same URL
  // params to walk the list.
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(() => Number(sp.get("skip")) || 0);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState({});
  const [q, setQ] = useState(() => sp.get("q") || "");
  const [qLive, setQLive] = useState(() => sp.get("q") || "");
  const [filters, setFilters] = useState(() => ({
    brand: sp.get("brand") || "",
    status: sp.getAll("status"),
    caliber: sp.getAll("caliber"),
    movement_type: sp.get("movement_type") || "",
    bin_location: sp.get("bin_location") || "",
  }));
  const [hideSold, setHideSold] = useState(() => sp.get("hideSold") !== "0");
  // "archive" view mode:
  //   "hide" (default) — active watches only
  //   "show" — active + archived mixed (with a badge on archived rows)
  //   "only" — archived only
  const [archiveMode, setArchiveMode] = useState(() => sp.get("archive") || "hide");
  const [showcaseReady, setShowcaseReady] = useState(() => sp.get("showcase") === "1");
  const [sort, setSort] = useState(() => sp.get("sort") || "excel_id");
  const [order, setOrder] = useState(() => sp.get("order") || "desc");

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { skip, limit: pageSize, sort, order };
    if (q) params.q = q;
    // Multi-value filters → pass arrays so axios serializes with repeated keys
    if (filters.status?.length) params.status = filters.status;
    if (filters.caliber?.length) params.caliber = filters.caliber;
    if (filters.brand) params.brand = filters.brand;
    if (filters.movement_type) params.movement_type = filters.movement_type;
    if (filters.bin_location) params.bin_location = filters.bin_location;
    if (hideSold) params.status_exclude = ["SOLD"];
    if (archiveMode === "show") params.include_archived = true;
    else if (archiveMode === "only") params.archived_only = true;
    if (showcaseReady) params.showcase_ready = true;
    fetchWatches(params)
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .finally(() => setLoading(false));
  }, [skip, sort, order, q, filters, hideSold, archiveMode, showcaseReady, pageSize]);

  useEffect(() => { fetchValidation().then(setValidation); }, []);
  useEffect(() => { load(); }, [load]);

  // Mirror filters → URL. `replace: true` so we don't pollute browser
  // history with every keystroke — the inventory page is one history
  // entry, the filter state lives in the query string.
  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (filters.brand) p.set("brand", filters.brand);
    filters.status.forEach((s) => p.append("status", s));
    filters.caliber.forEach((c) => p.append("caliber", c));
    if (filters.movement_type) p.set("movement_type", filters.movement_type);
    if (filters.bin_location) p.set("bin_location", filters.bin_location);
    if (!hideSold) p.set("hideSold", "0");
    if (archiveMode !== "hide") p.set("archive", archiveMode);
    if (showcaseReady) p.set("showcase", "1");
    if (sort !== "excel_id") p.set("sort", sort);
    if (order !== "desc") p.set("order", order);
    if (skip) p.set("skip", String(skip));
    setSp(p, { replace: true });
  }, [q, filters, hideSold, archiveMode, showcaseReady, sort, order, skip, setSp]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => { setQ(qLive); setSkip(0); }, 250);
    return () => clearTimeout(t);
  }, [qLive]);

  const setFilter = (k, v) => {
    // Array-valued (multi-select) filters just replace with the array;
    // single-valued ones use the legacy "__all__" sentinel for "no filter".
    setFilters((f) => ({ ...f, [k]: Array.isArray(v) ? v : (v === "__all__" ? "" : v) }));
    setSkip(0);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setQLive("");
    setSkip(0);
  };

  const onSort = (col) => {
    if (sort === col) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(col); setOrder("asc"); }
  };

  const onSaved = () => {
    setSheetOpen(false);
    setEditing(null);
    load();
    // Refresh validation so any custom values the user just typed (Status,
    // Jewels, Size, etc.) are available in the dropdowns immediately.
    fetchValidation().then(setValidation);
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteWatch(confirmDelete.id);
      toast.success(`Removed ${confirmDelete.brand || "watch"}`);
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const onArchive = async (w) => {
    try {
      if (w.archived) {
        await unarchiveWatch(w.id);
        toast.success(`${w.brand || "Watch"} restored from archive`);
      } else {
        await archiveWatch(w.id);
        toast.success(`${w.brand || "Watch"} archived — hidden from Inventory`, {
          action: {
            label: "Undo",
            onClick: () => unarchiveWatch(w.id).then(load).catch(() => toast.error("Undo failed")),
          },
        });
      }
      load();
    } catch {
      toast.error("Archive failed");
    }
  };

  const activeFilterCount =
    (filters.brand ? 1 : 0) +
    (filters.movement_type ? 1 : 0) +
    (filters.bin_location ? 1 : 0) +
    (filters.status?.length || 0) +
    (filters.caliber?.length || 0) +
    (q ? 1 : 0);

  return (
    <div className="px-6 md:px-10 py-8 pb-28 max-w-[1800px]">
      <InventoryHeader
        loading={loading}
        total={total}
        onAdd={() => { setEditing(null); setSheetOpen(true); }}
      />

      <InventoryFilterBar
        qLive={qLive}
        onSearchChange={setQLive}
        filters={filters}
        onFilterChange={setFilter}
        onClear={clearFilters}
        activeFilterCount={activeFilterCount}
        validation={validation}
        hideSold={hideSold}
        onHideSoldChange={(v) => { setHideSold(v); setSkip(0); }}
        archiveMode={archiveMode}
        onArchiveModeChange={(v) => { setArchiveMode(v); setSkip(0); }}
        showcaseReady={showcaseReady}
        onShowcaseReadyChange={(v) => { setShowcaseReady(v); setSkip(0); }}
        density={density}
        onDensityChange={setDensity}
      />

      <InventoryTable
        items={items}
        loading={loading}
        sort={sort}
        order={order}
        onSort={onSort}
        onOpen={(w) => nav(`/inventory/${w.id}?${sp.toString()}`)}
        onEdit={(w) => { setEditing(w); setSheetOpen(true); }}
        onDelete={(w) => setConfirmDelete(w)}
        onArchive={onArchive}
        skip={skip}
        total={total}
        page={pageSize}
        onFirst={() => setSkip(0)}
        onPrev={() => setSkip(Math.max(0, skip - pageSize))}
        onNext={() => setSkip(skip + pageSize)}
        onLast={() => setSkip(Math.max(0, Math.floor((total - 1) / pageSize) * pageSize))}
        density={density}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setSkip(0); }}
      />

      <WatchFormSheet
        open={sheetOpen}
        onOpenChange={(o) => { setSheetOpen(o); if (!o) setEditing(null); }}
        watch={editing}
        validation={validation}
        onSaved={onSaved}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">Remove from collection?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#3A2A10]">
              This will permanently delete{" "}
              <span className="text-[#0A0604]">{confirmDelete?.brand} {confirmDelete?.caliber}</span>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#8B6A1F]/45 rounded-sm" data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm" data-testid="delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
